import { useTranslation } from "react-i18next";
import {
	FrontendUserInfo,
	FrontendOrder,
	FrontendSampleTestBatch,
	FrontendSampleSpecification,
	FrontendProductOrder,
	FrontendProduct,
	FrontendOrderItem,
} from "node-library/dist/portal";
import {
	OrderItemType,
	OrderStatus,
	Severity,
	TurnaroundTime,
	UserType,
} from "node-library/dist/constants";
import {
	formatAbsoluteDate,
	formatCustomDate,
} from "node-library/dist/helpers/date_helper";
import React, { useEffect, useState } from "react";

import ItemPreviewTable from "../newOrder/itemPreviewTable";

import { checkUserTypeMeetsRequirement } from "../../../helpers/userHelper";
import { CustomDropdown } from "../../../common-components/inputs";
import {
	getAdminStatusColors,
	getAdminStatusDescriptionText,
	getAdminStatusString,
	getDefaultWarrantyItem,
	getPaymentStatusString,
	getStatusColors,
	getStatusDescriptionText,
	getStatusString,
	getWarrantyDueDate,
	getWarrantyTagTextColor,
} from "../../../helpers/orderHelper";
import KeyboardArrowLeftIconRounded from "@mui/icons-material/KeyboardArrowLeftRounded";
import { NavBar } from "../../../common-components/navbar";
import {
	SkyBlueButton,
	CustomIconButton,
	DropdownButton,
	BlueButton,
	WhiteButton,
} from "../../../common-components/button";
import {
	AlertIcon,
	InvoiceIcon,
	COAIcon,
	DownloadIcon,
	EditIcon,
	UserIcon,
	ReportsIcon,
	UploadIcon,
	ReceiptIcon,
	WrenchCircleIcon,
	DeliveryIcon,
} from "../../../common-components/svgIcons";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import {
	BackgroundColor,
	NeutralColor,
	PrimaryColor,
	SecondaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import { Card, Divider, Tooltip } from "@mui/material";
import { getContentContainer } from "../../../helpers/containerHelper";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import { sendGETRequest } from "../../../requestHelper/sendRequest";
import EditOrderStateTimeModal from "./admin_view/editOrderStateTimeModal";
import {
	calculateItemPrice,
	formatPrice,
	getShortTurnaroundTime,
	getTurnaroundTimeBackgroundColor,
} from "../../../helpers/sampleHelper";
import OrderDetailsContentSkeletonUI from "./skeletonUI/orderDetailsContentSkeleton";
import Restricted from "../../../permissionProvider/Restricted";
import { Permission } from "../../../permissionProvider/PermissionProvider";
import ItemRow from "./itemRow";
import { CustomDrawer } from "../../../common-components/drawers/drawer";
import ItemDetailDrawer from "./itemDetailDrawer";
import { set } from "date-fns";

interface Props {
	order: FrontendProductOrder;
	user: FrontendUserInfo | undefined;
	isInAdminView: boolean;
	itemList: Array<FrontendOrderItem>;

	goToEditOrderPage: (order: FrontendProductOrder) => void;
	goBackToViewOrdersPage: (reload: boolean) => void;
	discardUnstartedOrder: (order: FrontendProductOrder) => Promise<void>;
	viewOrderCOC: (order: FrontendProductOrder) => void;

	goToOrderReportPage: (order: FrontendProductOrder) => void;

	setDiscardOrderConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>;
	refreshSelectedOrder?: () => Promise<void>;
	setProceedAction: React.Dispatch<React.SetStateAction<() => void>>;
}

export default function OrderDetailsContent(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		order,
		isInAdminView,
		goBackToViewOrdersPage,
		goToOrderReportPage,
		user,
		itemList,
		viewOrderCOC,
		discardUnstartedOrder,
		setDiscardOrderConfirmOpen,
		setProceedAction,
	} = props;
	const [loading, setLoading] = useState<boolean>(false);

	//order updatable fields
	const [orderStatus, setOrderStatus] = useState<OrderStatus>(
		order.orderStatus
	);
	const [paid, setPaid] = useState<boolean>(order.paid);
	const [orderViewedByAdmin, setOrderViewedByAdmin] = useState<boolean>(
		order.orderViewedByAdmin
	);
	const [paymentTime, setPaymentTime] = useState<Date | undefined>(
		order.paymentTime ? new Date(order.paymentTime) : undefined
	);
	const [assignTime, setAssignTime] = useState<Date | undefined>(
		order.assigned ? new Date(order.assigned) : undefined
	);
	const [expectedCompleteTime, setExpectedCompleteTime] = useState<
		Date | undefined
	>(
		order.expectedCompleteTime
			? new Date(order.expectedCompleteTime)
			: undefined
	);

	const [shipTime, setShipTime] = useState<Date | undefined>(
		order.shipped ? new Date(order.shipped) : undefined
	);
	const [deliverTime, setDeliverTime] = useState<Date | undefined>(
		order.delivered ? new Date(order.delivered) : undefined
	);

	const [cocPreviewUrl, setCocPreviewUrl] = useState<string>();

	const [editStateTimeModalOpen, setEditStateTimeModalOpen] =
		useState<boolean>(false);

	const TATExpectedCompletionTimeMap: Map<TurnaroundTime, Date> = new Map();

	const warrantyItemList = itemList.filter(
		(item) =>
			item.itemType === OrderItemType.INSTRUMENT &&
			item.product.warranty.enabled
	);
	const [warrantyItem, setWarrantyItem] = useState<
		FrontendOrderItem | undefined
	>(
		warrantyItemList.length > 0
			? getDefaultWarrantyItem(warrantyItemList)
			: undefined
	);

	const [warrantyTagInfo, setWarrantyTagInfo] = useState<
		| {
				text: string;
				color: string;
		  }
		| undefined
	>(
		warrantyItem
			? getWarrantyTagTextColor(warrantyItem, translation, order.delivered)
			: undefined
	);

	const [selectedItem, setSelectedItem] = useState<FrontendOrderItem | null>(
		null
	);
	const [itemInfoDrawerOpen, setItemInfoDrawerOpen] = useState<boolean>(false);

	// productList.forEach((batch) => {
	// 	batch.sampleTestInfo.forEach((sample) => {
	// 		const tat: TurnaroundTime = sample.turnaroundTime;
	// 		const expectedCompleteTime: Date | undefined =
	// 			sample.expectedCompletionTime;
	// 		if (expectedCompleteTime) {
	// 			if (TATExpectedCompletionTimeMap.has(tat)) {
	// 				if (
	// 					expectedCompleteTime >
	// 					(TATExpectedCompletionTimeMap.get(tat) as Date)
	// 				) {
	// 					TATExpectedCompletionTimeMap.set(tat, expectedCompleteTime);
	// 				}
	// 			} else {
	// 				TATExpectedCompletionTimeMap.set(tat, expectedCompleteTime);
	// 			}
	// 		}
	// 	});
	// });

	const isBeyondEngineerView: boolean = checkUserTypeMeetsRequirement(
		user?.role as UserType,
		UserType.ENGINEER
	);

	let statusString: string;
	let statusColor: {
		backgroundColor: string;
		borderColor: string;
		textColor: string;
	};
	let statusDescriptionString: string;

	if (isBeyondEngineerView) {
		statusString = getAdminStatusString(orderStatus, order.paid, translation);
		statusDescriptionString = getAdminStatusDescriptionText(
			orderStatus,
			orderViewedByAdmin,
			translation
		);
		statusColor = getAdminStatusColors(
			orderStatus,
			orderViewedByAdmin,
			order.paid
		);
	} else {
		statusString = getStatusString(orderStatus, translation);
		statusDescriptionString = getStatusDescriptionText(
			orderStatus,
			translation
		);
		statusColor = getStatusColors(orderStatus);
	}

	//dates
	const submissionDateStr: string = order.submitted
		? formatCustomDate(order.submitted)
		: "";
	const assignDateStr: string = assignTime ? formatCustomDate(assignTime) : "";

	// const navbarItems: Array<{
	// 	label: string;
	// 	onClick: () => void;
	// 	isCurrentItem: boolean;
	// }> = [
	// 	{
	// 		label: translation("Orders"),
	// 		onClick: () => goBackToViewOrdersPage(false),
	// 		isCurrentItem: false,
	// 	},
	// 	{
	// 		label: translation("OrderDetails"),
	// 		onClick: () => {},
	// 		isCurrentItem: true,
	// 	},
	// ];

	const orderProgressItem: Array<{
		icon: React.ReactNode;
		infoRow: Array<{ title: string; info: React.ReactNode }>;
	}> = [
		{
			icon: <ReceiptIcon></ReceiptIcon>,
			infoRow: [
				{
					title: translation("OrderPaidOn"),
					info: (
						<div className="typography-body-01">
							{order.paymentTime ? formatCustomDate(order.paymentTime) : "--"}
						</div>
					),
				},
				{
					title: translation("PaymentStatus"),
					info: (
						<div
							className="typography-body-01"
							color={
								order.paid ? SemanticColor.SUCCESS_300 : SemanticColor.ERROR_300
							}
						>
							{order.paid
								? translation("Unpaid")
								: order.paymentStatus
								? getPaymentStatusString(order.paymentStatus, translation)
								: "--"}
						</div>
					),
				},
			],
		},
		{
			icon: <WrenchCircleIcon></WrenchCircleIcon>,
			infoRow: [
				{
					title: translation("EstimatedCompletionOn"),
					info: (
						<div className="typography-body-01">
							{order.expectedCompleteTime
								? formatCustomDate(order.expectedCompleteTime)
								: "--"}
						</div>
					),
				},
				{
					title: translation("Specialist"),
					info: order.engineerList
						? order.engineerList.map((engineer) => (
								<div className="engineer-tag typography-caption">{`${engineer.firstName} ${engineer.lastName}`}</div>
						  ))
						: "--",
				},
			],
		},
		{
			icon: <DeliveryIcon></DeliveryIcon>,
			infoRow: [
				{
					title: translation("TrackingNumber"),
					info: (
						<div className="typography-body-01">
							{order.shipmentTrackingNumber &&
							order.shipmentTrackingNumber.length > 0
								? order.shipmentTrackingNumber
								: "--"}
						</div>
					),
				},
				{
					title: translation("Carrier"),
					info: (
						<div className="typography-body-01">
							{order.carrier && order.carrier.length > 0 ? order.carrier : "--"}
						</div>
					),
				},
			],
		},
	];

	let actionButtonOptions: Array<{
		key: string;
		onClick: () => void;
		icon: React.ReactNode;
		text: string;
		widthToShow?: number;
	}> = [];
	let keyActionOption: {
		key: string;
		onClick: () => void;
		children: any;
		disabled?: boolean;
	} = {
		key: "edit-order",
		onClick: () => {
			props.goToEditOrderPage(order);
		},
		children: translation("Editorder"),
	};

	const headerItems = [
		{
			title: translation("Item"),
		},
		{
			title: translation("Unit Price"),
		},
		{
			title: translation("Quantity"),
		},
		{
			title: translation("Total"),
			permission: Permission.PRICE,
		},
		{
			title: translation("Type"),
		},
		{
			title: translation("Action"),
		},
	];

	useEffect(() => {
		// async function getCocPreviewUrl() {
		// 	if (order.orderStatus === OrderStatus.INCOMPLETE) return;
		// 	setLoading(true);
		// 	try {
		// 		const response = await sendGETRequest(
		// 			"/orders/get-order-coc-preview-url",
		// 			{
		// 				orderId: order.orderId,
		// 			}
		// 		);
		// 		setCocPreviewUrl(response.data.signedURL);
		// 	} catch (error) {
		// 		openSnackbar(
		// 			translation("SystemError"),
		// 			Severity.ERROR,
		// 			translation("SystemErrorMsg"),
		// 			<RedErrorIcon />
		// 		);
		// 	} finally {
		// 		setLoading(false);
		// 	}
		// }
		// getCocPreviewUrl();
		setOrderStatus(order.orderStatus);
		setOrderViewedByAdmin(order.orderViewedByAdmin);
		setPaymentTime(order.paymentTime ? new Date(order.paymentTime) : undefined);
		setAssignTime(order.assigned ? new Date(order.assigned) : undefined);
		setExpectedCompleteTime(
			order.expectedCompleteTime
				? new Date(order.expectedCompleteTime)
				: undefined
		);
		setShipTime(order.shipped ? new Date(order.shipped) : undefined);
		setDeliverTime(order.delivered ? new Date(order.delivered) : undefined);
	}, [order]);

	switch (orderStatus) {
		case OrderStatus.INCOMPLETE:
			actionButtonOptions = [
				{
					key: "discard-order",
					onClick: () => {
						setProceedAction(() => () => discardUnstartedOrder(order));
						setDiscardOrderConfirmOpen(true);
					},
					icon: <ClearRoundedIcon sx={{ fontSize: "20px" }} />,
					text: translation("Discardorder"),
				},
			];

			keyActionOption = {
				key: "edit-order",
				onClick: () => {
					props.goToEditOrderPage(order);
				},
				children: translation("Editorder"),
			};
			break;
		case OrderStatus.PENDING:
			actionButtonOptions = [
				...(isInAdminView
					? [
							// {
							// 	key: "upload-documents",
							// 	onClick: () => {
							// 		// setProceedAction(() => () => discardUnstartedOrder(order));
							// 		// setDiscardOrderConfirmOpen(true);
							// 	},
							// 	icon: <UploadIcon size={20} />,

							// 	text: translation("UploadDocuments"),
							// },
							// {
							// 	key: "download",
							// 	onClick: () => {
							// 		// setProceedAction(() => () => discardUnstartedOrder(order));
							// 		// setDiscardOrderConfirmOpen(true);
							// 	},
							// 	icon: <DownloadIcon size={20} />,

							// 	text: translation("DownloadDocuments"),
							// },
							{
								key: "update-order",
								onClick: () => {
									setEditStateTimeModalOpen(true);
								},
								icon: (
									<EditIcon
										strokeColor={NeutralColor.NEUTRAL_GREY_900}
										size={20}
									/>
								),

								text: translation("UpdateOrder"),
							},
							{
								key: "discard-order",
								onClick: () => {
									setProceedAction(() => () => discardUnstartedOrder(order));
									setDiscardOrderConfirmOpen(true);
								},
								icon: <ClearRoundedIcon sx={{ fontSize: "20px" }} />,

								text: translation("Discardorder"),
							},
					  ]
					: []),
			];
			if (!isBeyondEngineerView) {
				keyActionOption = {
					key: "view-order-results",
					onClick: () => {
						goToOrderReportPage(order);
					},
					disabled: true,
					children: translation("ViewResult"),
				};
			}
			break;
		case OrderStatus.IN_PROGRESS:
		// case OrderStatus.READY_TO_SHIP:
		case OrderStatus.SHIPPED:
		case OrderStatus.DELIVERED:
			actionButtonOptions = [
				{
					key: "view-coc",
					onClick: () => {
						viewOrderCOC(order as FrontendProductOrder);
					},
					widthToShow: 780,
					icon: <COAIcon />,

					text: translation("ChainOfCustody"),
				},
				...(isBeyondEngineerView
					? [
							{
								key: "update-order",
								onClick: () => {
									setEditStateTimeModalOpen(true);
								},
								icon: (
									<EditIcon
										strokeColor={NeutralColor.NEUTRAL_GREY_900}
										size={20}
									/>
								),

								text: translation("UpdateOrder"),
							},
							{
								key: "view-add-reports",
								onClick: () => {
									goToOrderReportPage(order);
								},
								icon: <UploadIcon size={20} />,

								text: translation("Uploadreports"),
							},
							...(isInAdminView
								? [
										{
											key: "discard-order",
											onClick: () => {
												setProceedAction(
													() => () =>
														discardUnstartedOrder(order as FrontendProductOrder)
												);
												setDiscardOrderConfirmOpen(true);
											},
											icon: (
												<ClearRoundedIcon
													sx={{ fontSize: "20px", strokeWidth: 3 }}
												/>
											),

											text: translation("Discardorder"),
										},
								  ]
								: []),
					  ]
					: []),
			];
			if (!isBeyondEngineerView) {
				keyActionOption = {
					key: "view-order-results",
					onClick: () => {
						goToOrderReportPage(order);
					},
					children: translation("ViewResult"),
				};
			}
			break;
	}

	let actionButton: React.ReactNode = <></>;
	if (actionButtonOptions.length == 1) {
		actionButton = (
			<WhiteButton
				id={"order-details-actionbutton"}
				buttonClassName={
					actionButtonOptions[0].widthToShow
						? "order-details-inprogress-action-button"
						: ""
				}
				onClick={(e) => {
					actionButtonOptions[0].onClick();
				}}
			>
				{actionButtonOptions[0].text}
			</WhiteButton>
		);
	} else if (actionButtonOptions.length > 1) {
		actionButton = (
			<DropdownButton
				buttonColor="white"
				id={"order-details-actionbutton"}
				container={getContentContainer()}
				menuOptions={actionButtonOptions.map((option) => {
					return {
						...option,
						children: (
							<div className="icon-dropdown-menu-item typography-body-01">
								{option.icon}
								{option.text}
							</div>
						),
					};
				})}
				onClick={(e) => {}}
			>
				{translation("Actions")}
			</DropdownButton>
		);
	}

	const buttonPanel: React.ReactNode = (
		<div
			style={{
				display: "flex",
				gap: "5px",
				justifyContent: "center",
				flexWrap: "wrap",
			}}
		>
			{actionButton}

			{!((user?.role as UserType) === UserType.ENGINEER) && (
				<BlueButton
					id={"order-details-key-action-button"}
					onClick={keyActionOption.onClick}
					disabled={keyActionOption.disabled}
				>
					{keyActionOption.children}
				</BlueButton>
			)}
		</div>
	);

	const orderDetailsHeaderRow: React.ReactNode = (
		<div className="order-detail-header">
			<div>
				<div
					style={{
						display: "flex",
						gap: "10px",
						alignItems: "center",
					}}
				>
					<CustomIconButton
						id="order-details-back-button"
						size="small"
						onClick={() => {
							goBackToViewOrdersPage(false);
						}}
						icon={<KeyboardArrowLeftIconRounded />}
					></CustomIconButton>
					<div
					// style={{
					// 	marginLeft: "38px",
					// }}
					>
						<div className="typography-body-02 grey-text">
							{translation("QuoteNo")}
						</div>
						<div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
							<div className="typography-heading-02 typography-bold">
								{order.quoteId && order.quoteId.length > 0
									? `#${order.quoteId}`
									: `--`}
							</div>
							<div
								className="typography-body-02 admin-order-status-box"
								style={{
									backgroundColor: statusColor.backgroundColor,
									border: `1px solid ${statusColor.borderColor}`,
									color: statusColor.textColor,
									minWidth: "20%",
								}}
							>
								{statusString}
							</div>
						</div>
					</div>

					{/* <NavBar items={navbarItems}></NavBar> */}
				</div>
			</div>
			{buttonPanel}
		</div>
	);

	const orderProgress: React.ReactNode = (
		<div className="order-progress-bar">
			<div className="typography-heading-04">
				{translation("OrderProgress")}
			</div>
			<div className="order-progress-bar-content">
				{" "}
				{orderProgressItem.map((item, index) => {
					return (
						<React.Fragment key={index}>
							{index !== 0 && (
								<hr
									className="step-bar-line"
									style={{
										width: 100 / (orderProgressItem.length * 2 - 1) + "%",
										border: "1px dashed #AFAFAF",
									}}
								></hr>
							)}
							<div
								style={{
									display: "flex",
									gap: "10px",
									minWidth: "max-content",
								}}
							>
								{item.icon}
								<div
									style={{
										display: "flex",
										flexDirection: "column",
										gap: "8px",
										minWidth: "max-content",
									}}
								>
									{item.infoRow.map((row) => (
										<div>
											<div className="typography-caption grey-text">
												{row.title}
											</div>
											{row.info}
										</div>
									))}
								</div>
							</div>
						</React.Fragment>
					);
				})}
			</div>
		</div>
	);

	const warrantyPriceRow: React.ReactNode = (
		<div className="order-warranty-price-row">
			{warrantyItem && warrantyTagInfo && (
				<Card
					className="order-warranty-card"
					sx={{
						background: "linear-gradient(to right, #062e3e, #107aa4)",
						flex: 6,
						minWidth: "max-content",
					}}
				>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
							<CustomDropdown
								id="warranty-item-dropdown"
								options={itemList
									.filter(
										(item) =>
											item.itemType === OrderItemType.INSTRUMENT &&
											item.product.warranty.enabled
									)
									.map((item) => {
										return {
											value: item,
											title: item.product.productName,
										};
									})}
								value={warrantyItem}
								setValue={setWarrantyItem}
								backgroundColor="transparent"
								color="white"
								smallSize
							/>
							<span className="typography-body-01" style={{ color: "white" }}>
								{translation("WarrantyUntil")}
							</span>
						</div>
						<div
							style={{ backgroundColor: warrantyTagInfo.color }}
							className="item-tag typography-caption"
						>
							{warrantyTagInfo.text}
						</div>
					</div>
					<div>
						<span
							className="typography-heading-04 typography-bold"
							style={{ color: "white" }}
						>{`${
							order.delivered
								? warrantyItem.product.warranty.dueOn
									? formatCustomDate(warrantyItem.product.warranty.dueOn)
									: "--"
								: translation("AwaitingDeliveryArrival")
						}`}</span>
						<span
							className="typography-body-01"
							style={{ color: SecondaryColor.SECONDARY_200 }}
						>{`${
							warrantyItem.product.warranty.period &&
							warrantyItem.product.warranty.unit
								? ` (${warrantyItem.product.warranty.period}-${
										warrantyItem.product.warranty.unit
								  } ${translation("warranty")})`
								: "--"
						}`}</span>
					</div>
				</Card>
			)}

			<Card
				className="order-warranty-card"
				sx={{
					background: PrimaryColor.PRIMARY_200,
					flex: 3,
					minWidth: "max-content",
				}}
			>
				<span className="typography-body-01">{translation("TotalPrice")}</span>
				<span className="typography-heading-04 typography-bold">{`${formatPrice(
					order.totalPrice,
					order.currency
				)} (${order.currency})`}</span>
			</Card>
		</div>
	);

	const clientInfoCard = isInAdminView ? (
		<div className="order-warranty-price-row">
			<Card
				className="order-detail-client-info-card"
				style={{ flex: 1, minWidth: "fit-content" }}
			>
				<div className="typography-body-01 typography-bold">
					{translation("Client")}
				</div>
				<div className="typography-body-01" style={{ display: "grid" }}>
					<div>{order.orderedBy}</div>
					<div>{order.clientCompanyName}</div>
					<Divider sx={{ my: 1 }}></Divider>
					<div>{order.userName}</div>
					<div>{order.phoneNumber}</div>
				</div>
			</Card>
			<Card className="order-detail-billing-info-card" style={{ flex: 2 }}>
				<div className="order-detail-billing-info-card-content">
					<div className="typography-body-01 typography-bold">
						{translation("BillingDetails")}
					</div>
					<div className="typography-body-01" style={{ display: "grid" }}>
						<div>{order.billingName?.trim() || "--"}</div>
						<div>{order.billingEmail?.trim() || "--"}</div>
						<div>{order.billingAddress?.trim() || "--"}</div>
						<div>
							{[
								order.billingCity?.trim() || "--",
								order.billingProvince?.trim() || "--",
							].join(", ")}
						</div>
						<div>
							{[
								order.billingCountry?.trim() || "--",
								order.billingPostalCode?.trim() || "--",
							].join(", ")}
						</div>
					</div>
				</div>
				<Divider orientation="vertical" sx={{ mx: "20px" }}></Divider>
				<div className="order-detail-billing-info-card-content">
					<div className="typography-body-01 typography-bold">
						{translation("ShippingDetails")}
					</div>
					<div className="typography-body-01" style={{ display: "grid" }}>
						<div>{order.shippingName?.trim() || "--"}</div>
						<div>{order.shippingPhoneNumber?.trim() || "--"}</div>
						<div>{order.shippingAddress?.trim() || "--"}</div>
						<div>
							{[
								order.shippingCity?.trim() || "--",
								order.shippingProvince?.trim() || "--",
							].join(", ")}
						</div>
						<div>
							{[
								order.shippingCountry?.trim() || "--",
								order.shippingPostalCode?.trim() || "--",
							].join(", ")}
						</div>
					</div>
				</div>
			</Card>
		</div>
	) : (
		<></>
	);

	const itemHeaderRowItems: React.ReactNode = headerItems.map((item, index) => {
		return (
			<Restricted
				key={item.title}
				to={item.permission ? (item.permission as Permission) : Permission.ALL}
			>
				<div
					style={{
						display: "flex",
						gap: "10px",
					}}
				>
					{index !== 0 && <Divider orientation="vertical" flexItem></Divider>}
					<div
						className="typography-body-01"
						style={{
							color: NeutralColor.NEUTRAL_GREY_400,
							display: "flex",
							alignItems: "center",
							gap: "5px",
						}}
					>
						{item.title}
						{/* {item.sortColumnBy && (
							<SortArrowIcon
								sortOrder={sortOrder}
								notInUse={sortBy !== item.sortColumnBy}
							/>
						)} */}
					</div>
				</div>
			</Restricted>
		);
	});
	// const orderDetailsCardRow: React.ReactNode = (
	// 	<div className="order-details-card-row">
	// 		<Card className="card-border-radius-14p card-medium-padding order-details-card">
	// 			<div className="order-details-header-card-content">
	// 				<div>
	// 					<div
	// 						style={{
	// 							display: "flex",
	// 							justifyContent: "space-between",
	// 							alignItems: "center",
	// 							gap: "10px",
	// 						}}
	// 					>
	// 						{isBeyondEngineerView && (
	// 							<div
	// 								style={{ cursor: "pointer" }}
	// 								onClick={() => {
	// 									setEditStateTimeModalOpen(true);
	// 								}}
	// 							>
	// 								<EditIcon strokeColor={NeutralColor.NEUTRAL_GREY_900} />
	// 							</div>
	// 						)}
	// 					</div>
	// 					<div
	// 						className="typography-caption"
	// 						style={{
	// 							color: NeutralColor.NEUTRAL_GREY_500,
	// 							marginTop: "5px",
	// 						}}
	// 					>
	// 						{statusDescriptionString}
	// 					</div>
	// 				</div>
	// 				<div>
	// 					<div className="order-details-time-grid">
	// 						<div className="order-details-time-grid-item">
	// 							<div
	// 								className="typography-caption"
	// 								style={{
	// 									color: NeutralColor.NEUTRAL_GREY_500,
	// 								}}
	// 							>
	// 								{translation("OrderSubmittedOn")}
	// 							</div>
	// 							<div className="typography-body-01 typography-bold">
	// 								{submissionDateStr ? submissionDateStr.split(" | ")[0] : "--"}
	// 							</div>
	// 							<div className="typography-caption">
	// 								{submissionDateStr ? submissionDateStr.split(" | ")[1] : ""}
	// 							</div>
	// 						</div>
	// 						<Divider orientation="vertical" flexItem></Divider>

	// 						<div className="order-details-time-grid-item">
	// 							<div
	// 								className="typography-caption"
	// 								style={{
	// 									color: NeutralColor.NEUTRAL_GREY_500,
	// 								}}
	// 							>
	// 								{translation("EngineerAssignedOn")}
	// 							</div>
	// 							<div className="typography-body-01 typography-bold">
	// 								{assignDateStr ? assignDateStr.split(" | ")[0] : "--"}
	// 							</div>
	// 							<div className="typography-caption">
	// 								{assignDateStr ? assignDateStr.split(" | ")[1] : ""}
	// 							</div>
	// 						</div>
	// 						<Divider orientation="vertical" flexItem></Divider>

	// 						<div className="order-details-time-grid-item">
	// 							<div
	// 								className="typography-caption"
	// 								style={{
	// 									color: NeutralColor.NEUTRAL_GREY_500,
	// 								}}
	// 							>
	// 								{translation("CompletedOn")}
	// 							</div>
	// 							<div
	// 								className="typography-body-01 typography-bold"
	// 								style={{
	// 									color: completeTime ? PrimaryColor.PRIMARY_500 : "sky blue",
	// 								}}
	// 							>
	// 								{completeTime
	// 									? formatCustomDate(completeTime).split(" | ")[0]
	// 									: "--"}
	// 							</div>
	// 						</div>

	// 						<Divider orientation="vertical" flexItem></Divider>

	// 						<div className="order-details-time-grid-item">
	// 							<div
	// 								className="typography-caption"
	// 								style={{
	// 									color: NeutralColor.NEUTRAL_GREY_500,
	// 								}}
	// 							>
	// 								{translation("ShipOn")}
	// 							</div>
	// 							<div
	// 								className="typography-body-01 typography-bold"
	// 								style={{
	// 									color: shipTime ? PrimaryColor.PRIMARY_500 : "sky blue",
	// 								}}
	// 							>
	// 								{shipTime ? formatCustomDate(shipTime).split(" | ")[0] : "--"}
	// 							</div>
	// 						</div>
	// 					</div>
	// 					<Divider orientation="horizontal" flexItem></Divider>
	// 					<div>
	// 						<div className="order-details-time-grid-item">
	// 							<div
	// 								className="typography-caption"
	// 								style={{
	// 									color: NeutralColor.NEUTRAL_GREY_500,
	// 								}}
	// 							>
	// 								{translation("EstimatedCompletionDate")}
	// 							</div>
	// 							<div className="order-details-eta-time-grid">
	// 								{TATExpectedCompletionTimeMap.size > 0 ? (
	// 									Array.from(TATExpectedCompletionTimeMap).map(
	// 										([key, value]) => {
	// 											const TATBackgroundColor: string =
	// 												getTurnaroundTimeBackgroundColor(key);

	// 											return (
	// 												<div key={key}>
	// 													<div
	// 														className="typography-caption turnaround-time-box"
	// 														style={{
	// 															backgroundColor: TATBackgroundColor,
	// 															width: "fit-content",
	// 														}}
	// 													>
	// 														{getShortTurnaroundTime(key, translation)}
	// 													</div>
	// 													<div className="typography-body-01">
	// 														{formatAbsoluteDate(value)}
	// 													</div>
	// 												</div>
	// 											);
	// 										}
	// 									)
	// 								) : (
	// 									<div
	// 										className="typography-body-01 typography-bold"
	// 										style={{ marginTop: "5px" }}
	// 									>
	// 										{"--"}
	// 									</div>
	// 								)}
	// 							</div>
	// 						</div>
	// 						<div className="order-details-time-grid-item">
	// 							<div
	// 								className="typography-caption"
	// 								style={{
	// 									color: NeutralColor.NEUTRAL_GREY_500,
	// 								}}
	// 							>
	// 								{translation("ClientReceived")}
	// 							</div>
	// 							<div
	// 								className="typography-body-01 typography-bold"
	// 								style={{
	// 									color: deliverTime ? PrimaryColor.PRIMARY_500 : "sky blue",
	// 								}}
	// 							>
	// 								{deliverTime
	// 									? formatCustomDate(deliverTime).split(" | ")[0]
	// 									: "--"}
	// 							</div>
	// 						</div>
	// 					</div>
	// 				</div>
	// 			</div>
	// 		</Card>
	// 		<Restricted to={Permission.CLIENT_INFO}>
	// 			<Card className="card-border-radius-14p card-medium-padding order-details-card">
	// 				<div className="order-details-client-billing-card-content">
	// 					<div>
	// 						<div
	// 							style={{
	// 								display: "flex",
	// 								alignItems: "center",
	// 								gap: "5px",
	// 							}}
	// 						>
	// 							<div className="order-details-header-icon-container">
	// 								<UserIcon
	// 									strokeColor={PrimaryColor.PRIMARY_500}
	// 									fillColor={PrimaryColor.PRIMARY_500}
	// 								></UserIcon>
	// 							</div>
	// 							<div className="typography-body-01 typography-bold">
	// 								{translation("ClientDetails")}
	// 							</div>
	// 						</div>

	// 						<div
	// 							style={{
	// 								marginTop: "30px",
	// 								display: "flex",
	// 								flexDirection: "column",
	// 								gap: "10px",
	// 							}}
	// 						>
	// 							<div className="order-details-user-info-row typography-body-02">
	// 								<div className="order-details-user-info-row-title">
	// 									{translation("Name")}
	// 								</div>
	// 								<div className="order-details-info-row-info">
	// 									{order.orderedBy}
	// 								</div>
	// 							</div>
	// 							<div className="order-details-user-info-row typography-body-02">
	// 								<div className="order-details-user-info-row-title">
	// 									{translation("Email")}
	// 								</div>
	// 								<div className="order-details-info-row-info">
	// 									{order.userName}
	// 								</div>
	// 							</div>
	// 							<div className="order-details-user-info-row typography-body-02">
	// 								<div className="order-details-user-info-row-title">
	// 									{translation("Phone")}
	// 								</div>
	// 								<div className="order-details-info-row-info">
	// 									{order.phoneNumber}
	// 								</div>
	// 							</div>
	// 							<div className="order-details-user-info-row typography-body-02">
	// 								<div className="order-details-user-info-row-title">
	// 									{translation("License")}
	// 								</div>
	// 							</div>
	// 						</div>
	// 					</div>

	// 					<Divider orientation="vertical" flexItem></Divider>
	// 					<div>
	// 						<div
	// 							style={{
	// 								display: "flex",
	// 								alignItems: "center",
	// 								gap: "5px",
	// 							}}
	// 						>
	// 							<div className="order-details-header-icon-container">
	// 								<InvoiceIcon></InvoiceIcon>
	// 							</div>
	// 							<div className="typography-body-01 typography-bold">
	// 								{translation("BillingDetails")}
	// 							</div>
	// 						</div>

	// 						<div
	// 							style={{
	// 								marginTop: "30px",
	// 								display: "flex",
	// 								flexDirection: "column",
	// 								gap: "10px",
	// 							}}
	// 						>
	// 							<div className="order-details-user-info-row typography-body-02">
	// 								<div className="order-details-user-info-row-title">
	// 									{translation("Name")}
	// 								</div>
	// 								<div className="order-details-info-row-info">
	// 									{order.billingName}
	// 								</div>
	// 							</div>
	// 							<div className="order-details-user-info-row typography-body-02">
	// 								<div className="order-details-user-info-row-title">
	// 									{translation("Email")}
	// 								</div>
	// 								<div className="order-details-info-row-info">
	// 									{order.billingEmail}
	// 								</div>
	// 							</div>
	// 							<div className="order-details-user-info-row typography-body-02">
	// 								<div className="order-details-user-info-row-title">
	// 									{translation("Phone")}
	// 								</div>
	// 								<div className="order-details-info-row-info">
	// 									{order.billingPhoneNumber}
	// 								</div>
	// 							</div>
	// 							<div className="order-details-user-info-row typography-body-02">
	// 								<div className="order-details-user-info-row-title">
	// 									{translation("Address")}
	// 								</div>
	// 								<div className="order-details-info-row-info">
	// 									<div>{`${order?.billingAddress}, `}</div>
	// 									<div>{`${order?.billingCity}, ${order?.billingProvince}, ${order?.billingCountry} `}</div>
	// 									<div>{order?.billingPostalCode}</div>
	// 								</div>
	// 							</div>

	// 							<div className="order-details-user-info-row typography-body-02">
	// 								<div className="order-details-user-info-row-title">
	// 									{translation("SendBillingCopiesTo")}
	// 								</div>
	// 							</div>
	// 						</div>
	// 					</div>
	// 				</div>
	// 			</Card>
	// 		</Restricted>
	// 	</div>
	// );

	let viewSampleTable: JSX.Element = (
		// <ItemPreviewTable
		// 	itemList={itemList}
		// 	isReadOnlyTable={true}
		// 	deleteItem={() => {}}
		// 	goToUpdateItem={() => {}}
		// 	currency={""}
		// 	shippingFee={0}
		// 	handlingFee={0}
		// 	handlingDescription={""}
		// 	shippingDescription={""}
		// 	fullServiceShippingSolution={false}
		// 	deleteHandlingFee={}
		// 	deleteShippingFee={props.deleteShippingFee}
		// 	goToUpdateHandlingShippingFee={goToAddShippingHandling}
		// />
		<></>
	);

	const cocCard: React.ReactNode = (
		<Card
			className="card-border-radius-14p card-medium-padding order-details-coa-card"
			style={{ cursor: "pointer" }}
			onClick={() => {
				viewOrderCOC(order);
			}}
		>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					gap: "5px",
					alignItems: "center",
				}}
			>
				<div className="typography-body-01">
					{translation("ChainOfCustody")}
				</div>
				<DownloadIcon />
			</div>
			{cocPreviewUrl && (
				<img
					style={{ width: "100%", height: "auto", marginTop: "10px" }}
					src={`${cocPreviewUrl}`}
					alt="COC Preview Image"
				></img>
			)}
		</Card>
	);

	const sampleDetailsBody: React.ReactNode =
		order.orderStatus === OrderStatus.INCOMPLETE ? (
			viewSampleTable
		) : (
			<div className="order-details-body">
				{viewSampleTable}
				{cocCard}
			</div>
		);

	return (
		<div
			style={{
				margin: "0 auto",
				height: "calc(100vh - 64px)",
				backgroundColor: BackgroundColor.WHITE,
			}}
		>
			{loading ? (
				<OrderDetailsContentSkeletonUI />
			) : (
				<div>
					{orderDetailsHeaderRow}
					{orderProgress}
					<div style={{ display: "grid", rowGap: "16px", padding: "24px" }}>
						{warrantyPriceRow}
						{clientInfoCard}
					</div>
					{/* {orderDetailsCardRow} */}
					<div className="item-list-view-header item-list-view-row">
						{itemHeaderRowItems}
					</div>
					{itemList.map((item) => (
						<ItemRow
							item={item}
							user={user}
							deleteOrder={() => {}}
							currency={order.currency}
							goToViewItemDetails={() => {
								setSelectedItem(item);
								setItemInfoDrawerOpen(!itemInfoDrawerOpen);
							}}
						/>
					))}

					{/* {sampleDetailsBody} */}

					<EditOrderStateTimeModal
						orderId={order.orderId}
						open={editStateTimeModalOpen}
						setOpen={setEditStateTimeModalOpen}
						orderStatus={orderStatus}
						setOrderStatus={setOrderStatus}
						orderViewedByAdmin={orderViewedByAdmin}
						setOrderViewedByAdmin={setOrderViewedByAdmin}
						isOrderPaid={order.paid}
						paymentTime={paymentTime}
						setPaymentTime={setPaymentTime}
						assignTime={assignTime}
						setAssignTime={setAssignTime}
						expectedCompleteTime={expectedCompleteTime}
						setExpectedCompleteTime={setExpectedCompleteTime}
						shipTime={shipTime}
						setShipTime={setShipTime}
						deliverTime={deliverTime}
						setDeliverTime={setDeliverTime}
						refreshSelectedOrder={props.refreshSelectedOrder}
					></EditOrderStateTimeModal>
					<ItemDetailDrawer
						open={itemInfoDrawerOpen}
						setOpen={setItemInfoDrawerOpen}
						item={selectedItem}
					></ItemDetailDrawer>
				</div>
			)}
		</div>
	);
}
